import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Form, Alert, ProgressBar, Modal, Spinner } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Badge from './Badge';
import api from '../services/api';

const QuizAttempt = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [quiz, setQuiz] = useState(null);
    const [attempt, setAttempt] = useState(null);
    const [answers, setAnswers] = useState({});
    const [timeLeft, setTimeLeft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [results, setResults] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const initializeQuiz = async () => {
            setLoading(true);
            try {
                const response = await api.post(`/api/quiz-attempts/start/${id}`);
                const { quiz: quizData, attempt, saved_answers, is_resumed, server_now } = response.data;
    
                setQuiz(quizData);
                setAttempt(attempt);
                setAnswers(saved_answers || {});
                setSubmitted(false);
                setShowResults(false);
                setError(null);
    
                // 🕒 Setup time limit
                if (quizData.time_limit) {
                    const timeLimitSeconds = quizData.time_limit * 60;
                    const startTime = new Date(attempt.started_at);
                    const now = server_now ? new Date(server_now) : new Date();
                    const elapsed = Math.floor((now - startTime) / 1000);
                    const remaining = Math.max(0, timeLimitSeconds - elapsed);
                    setTimeLeft(remaining);
                }
    
                // 🔁 Restore question index if resuming
                if (is_resumed && attempt.last_question_id) {
                    const lastIndex = quizData.questions.findIndex(q => q.id === attempt.last_question_id);
                    setCurrentQuestionIndex(lastIndex >= 0 ? lastIndex : 0);
                } else {
                    setCurrentQuestionIndex(0);
                }
            } catch (err) {
                if (err.response?.status === 403 && err.response?.data?.max_attempts !== undefined) {
                    setError(`You have reached the maximum number of attempts (${err.response.data.max_attempts}) for this quiz.`);
                } else {
                    setError(err.response?.data?.error || 'Failed to start quiz');
                }
                console.error('Error initializing quiz:', err);
            } finally {
                setLoading(false);
            }
        };
    
        initializeQuiz();
    }, [id]);

    const timerRef = useRef(null);

    useEffect(() => {
        if (timerRef.current) clearInterval(timerRef.current);
        
        if (timeLeft !== null && timeLeft > 0) {
            timerRef.current = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        // Auto-submit when time expires
                        if (!submitted && !submitting) {
                            handleSubmit();
                        }
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } else if (timeLeft === 0 && !submitted && !submitting) {
            // Handle case where time has already expired
            handleSubmit();
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [timeLeft, submitted, submitting]);

    const handleAnswerChange = async (questionId, answerId) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: answerId
        }));

        // Save progress
        try {
            await api.post(`/api/quiz-attempts/${attempt.id}/save-progress`, {
                question_id: questionId,
                answer_id: answerId
            });
        } catch (err) {
            console.error('Error saving progress:', err);
        }
    };

    const handleSubmit = async () => {
        if (submitted || submitting) return; // Prevent multiple submissions
        
        try {
            setSubmitting(true);
            // Convert answers object to array format expected by backend
            const answersArray = Object.entries(answers).map(([questionId, answerId]) => ({
                question_id: parseInt(questionId),
                selected_answer_id: parseInt(answerId)
            }));
            
            await api.post(`/api/quiz-attempts/${attempt.id}/submit`, { answers: answersArray });
            setSubmitted(true);
            navigate(`/quiz/${attempt.id}/results`);
        } catch (err) {
            setError('Failed to submit quiz');
            console.error('Error submitting quiz:', err);
        } finally {
            setSubmitting(false);
            setShowConfirmSubmit(false);
        }
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleNext = () => {
        if (currentQuestionIndex < quiz.questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    if (loading) {
        return <div className="text-center mt-5"><Spinner animation="border" /></div>;
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    if (!quiz || !quiz.questions || quiz.questions.length === 0) {
        return <Alert variant="warning">Quiz not found or has no questions</Alert>;
    }

    const currentQuestion = quiz.questions[currentQuestionIndex];
    if (!currentQuestion) {
        return <Alert variant="warning">Question not found</Alert>;
    }

    // Calculate remaining (skipped) questions
    const totalQuestions = quiz.questions.length;
    const attempted = Object.keys(answers).length;
    const remaining = totalQuestions - attempted;

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>{quiz.title}</h2>
                {timeLeft !== null && (
                    <div className="text-right">
                        <h4>Time Left: {formatTime(timeLeft)}</h4>
                    </div>
                )}
            </div>

            <div className="mb-3">
                <strong>Remaining:</strong> {remaining} / {totalQuestions}
            </div>

            <Card className="mb-4">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5>Question {currentQuestionIndex + 1} of {quiz.questions.length}</h5>
                        <div>
                            <Button 
                                variant="outline-primary" 
                                onClick={handlePrevious}
                                disabled={currentQuestionIndex === 0}
                                className="me-2"
                            >
                                Previous
                            </Button>
                            <Button 
                                variant="outline-primary" 
                                onClick={handleNext}
                                disabled={currentQuestionIndex === quiz.questions.length - 1}
                            >
                                Next
                            </Button>
                        </div>
                    </div>

                    <Card.Title className="mb-3">
                        <div className="fw-bold" dangerouslySetInnerHTML={{ __html: currentQuestion.question_text }} />
                    </Card.Title>
                    <div className="list-group">
                        {currentQuestion.answers.map(answer => (
                            <label 
                                key={answer.id} 
                                className={`list-group-item list-group-item-action ${answers[currentQuestion.id] === answer.id ? 'active' : ''}`}
                            >
                                <input
                                    type="radio"
                                    name={`question_${currentQuestion.id}`}
                                    value={answer.id}
                                    checked={answers[currentQuestion.id] === answer.id}
                                    onChange={() => handleAnswerChange(currentQuestion.id, answer.id)}
                                    className="me-2"
                                />
                                <div dangerouslySetInnerHTML={{ __html: answer.answer_text }} />
                            </label>
                        ))}
                    </div>
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-between">
                <Button 
                    variant="secondary" 
                    onClick={() => navigate('/quizzes')}
                >
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={() => setShowConfirmSubmit(true)}
                    disabled={submitting}
                >
                    {submitting ? 'Submitting...' : 'Submit Quiz'}
                </Button>
            </div>

            <Modal show={showConfirmSubmit} onHide={() => setShowConfirmSubmit(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Submission</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to submit this quiz? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmSubmit(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? 'Submitting...' : 'Yes, Submit Quiz'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default QuizAttempt; 